import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import LoaderSkeleton from '~/shared/components/LoaderSkeleton'

interface BadgeCardProps {
  title: string
  subTitle: string
  numberValue?: number
  value?: string
  isLeagueBadge?: boolean
  srcKey: string
  isSlim?: boolean
  fullWidthInSmallScreen?: boolean
  leagueRank?: number
  isLoading?: boolean
}

export default function BadgeCard({
  title,
  subTitle,
  value,
  numberValue,
  isLeagueBadge,
  srcKey,
  isSlim,
  fullWidthInSmallScreen,
  leagueRank,
  isLoading,
}: BadgeCardProps): React.ReactElement {
  const { t } = useTranslation('statisticsV2')
  const isUnranked = isLeagueBadge && (leagueRank === undefined || leagueRank === null || leagueRank === 0)
  const valueFontClass = numberValue !== undefined && numberValue < 100 ? 'text-11' : 'text-6'
  const subTitleFontClass = isSlim || subTitle.length > 40 ? 'text-xxs' : subTitle.length > 22 ? 'text-xs' : 'text-md'
  const fontClass = value === 'A' || value === '6' ? 'text-green-lime' : 'text-blue-blueberry'

  return (
    <div
      className={`flex  ${
        isSlim ? 'w-full min-w-full lg:min-w-62 lg:w-62' : fullWidthInSmallScreen ? 'w-full lg:w-72' : 'w-84 lg:w-72'
      }  shrink-0 overflow-hidden rounded-4 bg-purple-charcoal/60 border border-white/5 ${
        isLeagueBadge ? (isSlim ? 'min-h-11' : 'min-h-20 lg:min-h-24') : isSlim ? 'min-h-11' : 'min-h-20'
      }`}
    >
      {isLoading ? (
        <LoaderSkeleton className="absolute min-h-full min-w-full rounded bg-transparent" randomStart speed={1200} />
      ) : (
        <div
          className={`flex justify-center items-center gap-3 min-w-full min-h-full ${
            isSlim ? 'px-2 py-1' : 'px-4 py-3'
          }`}
        >
          <div className="flex min-w-0 flex-1 gap-2">
            <div
              className={`flex relative shrink-0 justify-center items-center bg-black ${
                isSlim ? 'w-8 h-8' : 'w-12 h-12'
              } rounded`}
            >
              <ResponsiveImage
                alt="Certificate"
                fit="contain"
                height={48}
                imageClassName={`w-14 md:w-11 ${isSlim ? 'w-8 h-8' : 'w-12 h-12'}`}
                srcKey={`${srcKey && !isUnranked ? srcKey : 'gamification/badges/league/bronze-badge.svg'}`}
                width={48}
              />
              {isLeagueBadge && (
                <p className="absolute flex justify-center items-center font-bold h-full text-add-gradient bg-badge-bronze -mt-1 text-4 leading-2.5 md:leading-4.5 md:text-3 lg:text-xl">
                  {!isUnranked ? `#${leagueRank}` : 'H'}
                </p>
              )}
            </div>
            <div className="flex min-w-0 flex-grow flex-col justify-center">
              <p className="text-gray-lavender leading-4 text-xxs">{title.toUpperCase()}</p>
              <p className={`break-words leading-tight text-white ${subTitleFontClass} font-bold`}>{subTitle}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end">
            {numberValue !== undefined ? (
              <p className={`${valueFontClass} font-bold leading-8 text-green-lime`}>{numberValue}</p>
            ) : (
              <p
                className={`${!isUnranked ? (isSlim ? 'text-lg' : 'text-8') : 'text-4'} font-bold leading-7 ${
                  !isLeagueBadge ? fontClass : 'text-green-lime'
                }`}
              >
                {isUnranked ? t('unranked') : value}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
